import { fetchHTML } from '@utils';
import { pubsub } from '@services';
import { d as debounce } from './debounce-Dg7s1ivP.js';

class PredictiveSearchResults extends HTMLElement {
  #unsubscribers = [];
  #drawerId = this.closest('drawer-element')?.id;
  #abortController;
  #swipers = [];
  #options = {
    minimumSearchTermLength: 2,
    searchId: this.dataset.searchId,
    hideElement: this.dataset.hideElement,
    resourcesLimit: 10,
    resourcesLimitScope: 'each',
  };
  #search = document.querySelector(`#${this.#options.searchId}`);
  #elementsToHide;

  connectedCallback() {
    // Get the search form
    if (!this.#search) return;

    if (this.#options.hideElement) {
      this.#elementsToHide = document.querySelector(`#${this.#options.hideElement}`);
    }

    // Handle search clear button
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.#search.clearSearchInput();
      }
    });

    // Handle search input
    this.#unsubscribers.push(
      pubsub.subscribe('search:input', ({ id, data: { value } }) => {
        if (id !== this.dataset.searchId) return;
        this.#enableLoaders();
        this.#updateSearch(value);
      }),
      pubsub.subscribe('search:clear', ({ id }) => {
        id === this.dataset.searchId && this.#onClearSearchInput();
      }),
      this.#drawerId &&
        pubsub.subscribe('drawer:open', ({ id }) => {
          if (id !== this.#drawerId) return;
          this.#search.clearSearchInput();
          this.#clearSearchResults();
        })
    );
  }

  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.#destroySwipers();
  }

  /**
   * Clear the search results
   */
  #clearSearchResults() {
    this.innerHTML = '';
  }

  /**
   * Clear the search input
   */
  #onClearSearchInput() {
    this.ariaHidden = true;
    this.#hideOnSearchResults(false);
  }

  /**
   * Enable loaders
   */
  #enableLoaders() {
    this.querySelectorAll('[data-loader]').forEach((loader) => {
      loader.ariaHidden = false;
    });
  }

  /**
   * Disable loaders
   */
  #disableLoaders() {
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.ariaHidden = true;
      });
    }, 50);
  }

  /**
   * Update the search
   */
  #updateSearch = debounce((searchTerm) => this.#getSearchResults(searchTerm), 300);

  /**
   * Get the search results
   */
  #getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.#options.minimumSearchTermLength) {
      this.#clearSearchResults();
      this.#hideOnSearchResults(false);
      this.ariaHidden = true;
      return;
    }
    this.#fetchSearchResults(searchTerm);
  }

  /**
   * Hide other elements when the predictive search results are shown
   */
  #hideOnSearchResults(hide = true) {
    if (this.#elementsToHide) this.#elementsToHide.ariaHidden = hide;
  }

  /**
   * Fetch the search results
   */
  async #fetchSearchResults(searchTerm) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const searchUrl = routes.predictive_search_url;
      const html = await fetchHTML(
        `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search&resources[limit]=${this.#options.resourcesLimit}&resources[limit_scope]=${this.#options.resourcesLimitScope}&resources[options][fields]=title,product_type,body,variants.title`,
        { signal: this.#abortController.signal }
      );

      const resultsMarkup = html.querySelector('predictive-search-results').innerHTML;
      this.innerHTML = '';
      this.innerHTML = resultsMarkup;
      this.ariaHidden = false;
      this.#hideOnSearchResults(true);
      this.#initSwipers();
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      this.#disableLoaders();
    }
  }

  /**
   * Initialize Swiper instances for suggestions
   */
  async #initSwipers() {
    this.#destroySwipers();

    const swiperElements = this.querySelectorAll('[data-swiper-search-suggestions]');
    if (swiperElements.length === 0) return;

    try {
      const { Swiper } = await import('@services');
      const { FreeMode } = await import('./index-CPEhx4uO.js');

      swiperElements.forEach((element) => {
        const swiper = new Swiper(element, {
          modules: [FreeMode],
          slidesPerView: 'auto',
          spaceBetween: 8,
          freeMode: {
            enabled: true,
            sticky: false,
          },
        });
        this.#swipers.push(swiper);
      });
    } catch (error) {
      console.error('Error initializing Swiper:', error);
    }
  }

  /**
   * Destroy all Swiper instances
   */
  #destroySwipers() {
    this.#swipers.forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.#swipers = [];
  }
}

customElements.define('predictive-search-results', PredictiveSearchResults);
